import { useState } from "react";
import { Search, Moon, Sun, Bell, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useTheme } from "@/components/ui/theme-provider";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Logo } from "@/components/logo";

export function Header() {
  const { theme, setTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [monitoringEnabled, setMonitoringEnabled] = useState(true);
  const [, navigate] = useLocation();

  const { data: user } = useQuery({
    queryKey: ["/api/user"],
  }) as { data: { name?: string } | undefined };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      // Navigate to domains page with search query
      navigate(`/domains?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const performSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/domains?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const getInitials = (name: string) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  return (
    <header className="bg-gradient-to-r from-purple-600 to-teal-700 text-white shadow-lg">
      <div className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Logo size="sm" variant="white" showText={false} />
          <div>
            <h1 className="text-xl font-bold">PCI Scan Report</h1>
            <p className="text-sm opacity-90">JavaScript Integrity & Compliance Monitor</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="hidden md:flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={performSearch}
              className="p-1 hover:bg-white/10 rounded"
              data-testid="button-search"
            >
              <Search className="w-4 h-4 text-white/70 cursor-pointer" />
            </Button>
            <Input
              type="text"
              placeholder="Search across all sites"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
              className="bg-transparent border-none placeholder-white/70 text-white focus:outline-none focus:ring-0 w-64"
              data-testid="input-search"
            />
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
          >
            {theme === "light" ? (
              <Moon className="w-4 h-4" />
            ) : (
              <Sun className="w-4 h-4" />
            )}
          </Button>
          
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              {user?.name ? (
                <span className="text-sm font-semibold">{getInitials(user.name)}</span>
              ) : (
                <User className="w-4 h-4" />
              )}
            </div>
            <span className="text-sm">{user?.name || "User"}</span>
          </div>
          
          <div className="relative">
            <Button variant="ghost" size="sm" className="p-2 hover:bg-white/10 rounded-lg">
              <Bell className="w-4 h-4" />
            </Button>
            <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1 min-w-[1.25rem] h-5 flex items-center justify-center">
              4
            </Badge>
          </div>
        </div>
      </div>
      
      {/* Analysis Progress Banner */}
      <div className="bg-white/10 px-6 py-3 border-t border-white/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {monitoringEnabled && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <span className="text-sm">
              {monitoringEnabled 
                ? "Daily script integrity monitoring active. Real-time change detection enabled."
                : "Daily script integrity monitoring disabled. Real-time change detection disabled."
              }
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Switch
                checked={monitoringEnabled}
                onCheckedChange={setMonitoringEnabled}
                className="data-[state=checked]:bg-purple-400"
              />
              <span className="text-sm text-white/80">
                {monitoringEnabled ? "ON" : "OFF"}
              </span>
            </div>
            <Button 
              onClick={() => navigate('/user-inputs')}
              className="bg-purple-400 text-slate-900 px-4 py-1 rounded text-sm font-medium hover:bg-emerald-300 transition-colors"
            >
              User Inputs
            </Button>
            <Button 
              onClick={() => navigate('/change-history')}
              className="bg-purple-400 text-slate-900 px-4 py-1 rounded text-sm font-medium hover:bg-emerald-300 transition-colors"
            >
              Change History
            </Button>
            <Button 
              onClick={() => navigate('/scripts')}
              className="bg-purple-400 text-slate-900 px-4 py-1 rounded text-sm font-medium hover:bg-emerald-300 transition-colors"
            >
              View Details
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
